import { openDB, IDBPDatabase } from 'idb';
import type { BoundingBox, OfflineRegion } from '@/types';
import config from '@/config';

const TILE_DB_NAME = 'tensormaps-tiles';
const TILE_DB_VERSION = 1;
const TILE_STORE_NAME = 'tiles';
const METADATA_STORE_NAME = 'metadata';

interface TileRecord {
  key: string; // z/x/y
  data: ArrayBuffer;
  timestamp: number;
  regionId?: string;
}

class TileService {
  private db: IDBPDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.initDB();
    return this.initPromise;
  }

  private async initDB(): Promise<void> {
    this.db = await openDB(TILE_DB_NAME, TILE_DB_VERSION, {
      upgrade(db) {
        // Tiles store
        if (!db.objectStoreNames.contains(TILE_STORE_NAME)) {
          const tileStore = db.createObjectStore(TILE_STORE_NAME, {
            keyPath: 'key',
          });
          tileStore.createIndex('regionId', 'regionId');
          tileStore.createIndex('timestamp', 'timestamp');
        }

        // Metadata store
        if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
          db.createObjectStore(METADATA_STORE_NAME, { keyPath: 'id' });
        }
      },
    });
  }

  async getTile(z: number, x: number, y: number): Promise<ArrayBuffer | null> {
    await this.init();
    if (!this.db) return null;

    const key = `${z}/${x}/${y}`;
    const record = await this.db.get(TILE_STORE_NAME, key);
    return record?.data ?? null;
  }

  async putTile(
    z: number,
    x: number,
    y: number,
    data: ArrayBuffer,
    regionId?: string
  ): Promise<void> {
    await this.init();
    if (!this.db) return;

    const key = `${z}/${x}/${y}`;
    const record: TileRecord = {
      key,
      data,
      timestamp: Date.now(),
      regionId,
    };

    await this.db.put(TILE_STORE_NAME, record);
  }

  async deleteTile(z: number, x: number, y: number): Promise<void> {
    await this.init();
    if (!this.db) return;

    const key = `${z}/${x}/${y}`;
    await this.db.delete(TILE_STORE_NAME, key);
  }

  async deleteRegionTiles(regionId: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    const tx = this.db.transaction(TILE_STORE_NAME, 'readwrite');
    const index = tx.store.index('regionId');
    let cursor = await index.openCursor(IDBKeyRange.only(regionId));

    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }

    await tx.done;
  }

  async getStorageInfo(): Promise<{ used: number; available: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage ?? 0,
        available: estimate.quota ?? 0,
      };
    }
    return { used: 0, available: 0 };
  }

  async downloadRegion(
    region: OfflineRegion,
    onProgress: (progress: number) => void
  ): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    // Try bulk download first (more efficient)
    const bulkSuccess = await this.downloadRegionBulk(region, onProgress);
    if (bulkSuccess) return;

    // Fallback to individual tile downloads
    const tiles = this.getTilesInBounds(
      region.bounds,
      region.minZoom,
      region.maxZoom
    );

    const totalTiles = tiles.length;
    let downloadedTiles = 0;

    const batchSize = 10;
    for (let i = 0; i < tiles.length; i += batchSize) {
      const batch = tiles.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async ({ z, x, y }) => {
          try {
            const url = this.getTileUrl(z, x, y);
            const response = await fetch(url);

            if (response.ok) {
              const data = await response.arrayBuffer();
              await this.putTile(z, x, y, data, region.id);
            }
          } catch (error) {
            console.error(`Failed to download tile ${z}/${x}/${y}:`, error);
          }

          downloadedTiles++;
          onProgress(downloadedTiles / totalTiles);
        })
      );
    }
  }

  private async downloadRegionBulk(
    region: OfflineRegion,
    onProgress: (progress: number) => void
  ): Promise<boolean> {
    const baseUrl = config.api.tileServerUrl;
    const bulkUrl = `${baseUrl}/bulk/tiles`;

    try {
      const response = await fetch(bulkUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'openmaptiles',
          format: 'pbf',
          minZoom: region.minZoom,
          maxZoom: region.maxZoom,
          bounds: {
            north: region.bounds.ne.lat,
            south: region.bounds.sw.lat,
            east: region.bounds.ne.lng,
            west: region.bounds.sw.lng,
          },
        }),
      });

      if (!response.ok || !response.body) {
        return false;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let downloadedCount = 0;
      let totalCount = this.estimateTileCount(region.bounds, region.minZoom, region.maxZoom);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const tileData = JSON.parse(line);

            if (tileData.complete) {
              totalCount = tileData.totalCount;
              continue;
            }

            if (tileData.z !== undefined && tileData.data) {
              const binaryData = this.base64ToArrayBuffer(tileData.data);
              await this.putTile(tileData.z, tileData.x, tileData.y, binaryData, region.id);
              downloadedCount++;
              onProgress(downloadedCount / totalCount);
            }
          } catch (e) {
            console.warn('Failed to parse tile data:', e);
          }
        }
      }

      return downloadedCount > 0;
    } catch (error) {
      console.warn('Bulk download failed, will use fallback:', error);
      return false;
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private getTilesInBounds(
    bounds: BoundingBox,
    minZoom: number,
    maxZoom: number
  ): { z: number; x: number; y: number }[] {
    const tiles: { z: number; x: number; y: number }[] = [];

    for (let z = minZoom; z <= maxZoom; z++) {
      const minTile = this.lngLatToTile(bounds.sw.lng, bounds.ne.lat, z);
      const maxTile = this.lngLatToTile(bounds.ne.lng, bounds.sw.lat, z);

      for (let x = minTile.x; x <= maxTile.x; x++) {
        for (let y = minTile.y; y <= maxTile.y; y++) {
          tiles.push({ z, x, y });
        }
      }
    }

    return tiles;
  }

  private lngLatToTile(
    lng: number,
    lat: number,
    zoom: number
  ): { x: number; y: number } {
    const n = Math.pow(2, zoom);
    const x = Math.floor(((lng + 180) / 360) * n);
    const latRad = (lat * Math.PI) / 180;
    const y = Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
    );
    return { x, y };
  }

  private getTileUrl(z: number, x: number, y: number): string {
    const baseUrl = config.api.tileServerUrl;
    return `${baseUrl}/data/openmaptiles/${z}/${x}/${y}.pbf`;
  }

  estimateTileCount(bounds: BoundingBox, minZoom: number, maxZoom: number): number {
    return this.getTilesInBounds(bounds, minZoom, maxZoom).length;
  }

  estimateDownloadSize(tileCount: number): number {
    // Average tile size is approximately 20KB for vector tiles
    return tileCount * 20 * 1024;
  }
}

export const tileService = new TileService();
